import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { coachApi, coachingApi } from "@bw/shared/api";
import type { CoachingAmendmentContext, CoachingAvailabilityCheckInput, CoachingProposal, CoachingProposalInput, CoachingWorkspaceItem } from "@bw/shared/api";
import { useAuth, useCoachingAmendmentContext, useCoachingAvailabilityCheck, useCoachingProposal, useIssueCoachingProposal } from "@bw/shared/hooks";
import { asMoney } from "@bw/shared/types";
import { coachingCancellationSummary, formatMoney } from "@bw/shared/utils";
import { fromZonedDateTimeInput, toZonedDateTimeInput } from "@/libs/zonedDateTime";
import { CoachingAvailabilityGrid } from "./coaching-availability-grid";
import { expandScheduleBatch, materializeSessions, MAX_PROPOSAL_SESSIONS,
  type BatchDraft, type ScheduledWindow, type SessionEntry } from "./coaching-schedule-draft";

const schema = z.object({
  source: z.string(), serviceId: z.string(), name: z.string().trim().min(1, "Enter a proposal name."),
  amount: z.string().regex(/^\d+(?:\.\d{1,2})?$/, "Enter a valid total price."), templateName: z.string(),
});
type Values = z.infer<typeof schema>;
type Context = NonNullable<CoachingWorkspaceItem["authoring"]>;
class ProposalInputError extends Error {}
const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
function moneyOrNull(value: string) {
  return /^\d+(?:\.\d{1,2})?$/.test(value) ? asMoney(value) : null;
}

function initialBatch(context: Context): BatchDraft {
  const preferred = toZonedDateTimeInput(context.preferred_datetime, context.timezone);
  const localNow = toZonedDateTimeInput(new Date().toISOString(), context.timezone);
  const date = preferred.slice(0, 10) || localNow.slice(0, 10);
  return { date, time: preferred.slice(11, 16), exactStart: context.preferred_datetime ?? undefined,
    duration: "60", courtId: "", repeat: "Does not repeat", every: "1", unit: "week", weekdays: [],
    ends: "After occurrences", occurrences: "4", endDate: "" };
}

function sessionSummary(session: ScheduledWindow, context: Context): string {
  const starts = new Date(session.starts_at).toLocaleString("en-US", { timeZone: context.timezone,
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  const ends = new Date(session.ends_at).toLocaleTimeString("en-US", { timeZone: context.timezone,
    hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  const court = session.court_ids.length
    ? session.court_ids.map((id) => context.courts.find((item) => item.court_id === id)?.name ?? "Court unavailable").join(", ")
    : "No court required";
  return `${starts} – ${ends} · ${court}`;
}

export function CoachingProposalComposer({ requestId, onClose, sourceOffer, amendmentAcceptanceId }: { requestId: string;
  onClose: (sent?: boolean) => void; sourceOffer?: CoachingProposal; amendmentAcceptanceId?: string }) {
  const { user } = useAuth();
  const context = useQuery({ queryKey: ["coaching", user?.uid, "authoring", requestId],
    queryFn: ({ signal }) => coachingApi.getWorkspace(requestId, signal), retry: false, staleTime: 0 });
  const amendment = useCoachingAmendmentContext(requestId, amendmentAcceptanceId ?? "", Boolean(amendmentAcceptanceId));
  if (context.isError) return <Alert severity="error">Proposal options are unavailable. Refresh and try again.</Alert>;
  if (!context.data) return <Typography role="status">Loading proposal options…</Typography>;
  if (amendmentAcceptanceId && amendment.isError) return <Alert severity="error">The remaining lesson schedule changed. Refresh before rescheduling.</Alert>;
  if (amendmentAcceptanceId && !amendment.data) return <Typography role="status">Loading remaining lessons…</Typography>;
  if ((!amendmentAcceptanceId && !context.data.can_issue) || (amendmentAcceptanceId && !context.data.can_amend) || !context.data.authoring)
    return <Alert severity="info">This request cannot receive this proposal.</Alert>;
  return <ProposalForm key={`${user?.uid}:${requestId}:${context.data.authoring.expected_revision}:${context.data.revision_id}`}
    requestId={requestId} facilityId={context.data.facility_id} context={context.data.authoring}
    onClose={onClose} refreshing={context.isFetching}
    previousRevisionId={context.data.can_view_proposal ? context.data.revision_id : null}
    sourceOffer={sourceOffer?.request_id !== requestId && sourceOffer?.terms.facility_id === context.data.facility_id
      ? sourceOffer : undefined} amendment={amendment.data} />;
}

function ProposalForm({ requestId, facilityId, context, onClose, refreshing, previousRevisionId, sourceOffer, amendment }: {
  requestId: string; facilityId: string; context: Context; onClose: (sent?: boolean) => void;
  refreshing: boolean; previousRevisionId: string | null; sourceOffer?: CoachingProposal; amendment?: CoachingAmendmentContext;
}) {
  const previous = useCoachingProposal(requestId, previousRevisionId ?? "", Boolean(previousRevisionId));
  const previousMatches = previous.data?.request_id.toLowerCase() === requestId.toLowerCase()
    && previous.data?.revision_id.toLowerCase() === previousRevisionId?.toLowerCase();
  const previousTerms = sourceOffer?.terms ?? (previous.isCurrent && previousMatches ? previous.data?.terms : undefined);
  const flexibleAmendment = Boolean(amendment && previousTerms?.schedule.kind === "flexible_package");
  const previousAvailable = Boolean(sourceOffer || (previous.isCurrent && previousMatches));
  const previousSessions = previousTerms?.schedule.kind === "fixed" ? previousTerms.schedule.sessions : null;
  const previousDurations = previousSessions?.map((session) => (Date.parse(session.ends_at) - Date.parse(session.starts_at)) / 60_000);
  const previousDuration = previousDurations?.[0];
  const previousReason = !sourceOffer && (previous.isError || (previous.data && !previousMatches)) ? "unavailable"
    : previousTerms && previousTerms.currency.toLowerCase() !== context.currency.toLowerCase()
      ? "another currency unavailable" : previousTerms && previousTerms.schedule.kind !== "fixed"
        ? "flexible package unavailable" : previousDurations && (previousDurations.length > MAX_PROPOSAL_SESSIONS
          || !previousDuration || !Number.isSafeInteger(previousDuration) || previousDuration < 15 || previousDuration > 480)
          ? "previous schedule unavailable" : null;
  const [batch, setBatch] = useState<BatchDraft>(() => initialBatch(context));
  const [entries, setEntries] = useState<SessionEntry[]>([]);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateDefaults, setTemplateDefaults] = useState({ duration: "60", courtId: "" });
  const [sourceWarning, setSourceWarning] = useState<string | null>(null);
  const sourceRevision = useRef(sourceOffer?.revision_id ?? null);
  const issue = useIssueCoachingProposal();
  const queryClient = useQueryClient();
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const amendmentInitialized = useRef(false);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: {
    source: "", serviceId: "", name: "", amount: "", templateName: "",
  } });
  const values = form.watch();

  useEffect(() => {
    if (!amendment || amendmentInitialized.current || !previousTerms) return;
    amendmentInitialized.current = true;
    form.reset({ source: "previous", serviceId: previousTerms.source_service_id ?? "",
      name: previousTerms.service_name, amount: String(amendment.remaining_value), templateName: "" });
    if (previousTerms.schedule.kind === "flexible_package") {
      setEntries([]);
      setSourceWarning("Remaining credits, session duration, booking window, and court requirements stay unchanged. Any booked remaining session returns to an available credit after acceptance so the player can book a new time.");
      return;
    }
    const remaining = amendment.remaining_units.flatMap((unit) => {
      if (unit.kind !== "session" || !unit.starts_at || !unit.ends_at) return [];
      const starts = toZonedDateTimeInput(unit.starts_at, context.timezone);
      const duration = (Date.parse(unit.ends_at) - Date.parse(unit.starts_at)) / 60_000;
      return [{ id: crypto.randomUUID(), localStart: starts.slice(0, 16), exactStart: unit.starts_at,
        duration: String(duration), courtId: unit.court_ids[0] ?? "none",
        courtIds: unit.court_ids.length > 1 ? [...unit.court_ids] : undefined } satisfies SessionEntry];
    });
    if (remaining.length !== amendment.remaining_units.length) return;
    setEntries(remaining);
    const first = remaining[0];
    if (first) setBatch((current) => ({ ...current, date: first.localStart.slice(0, 10),
      time: first.localStart.slice(11, 16), exactStart: first.exactStart,
      duration: first.duration, courtId: first.courtId }));
    setSourceWarning("Dates and times can change. Single-court sessions can choose another court; multi-court assignments stay together. Price, duration, session count, and participants remain unchanged.");
  }, [amendment, previousTerms, context.timezone, form]);

  useEffect(() => {
    if (amendment) return;
    const externalChanged = sourceRevision.current !== null && sourceRevision.current !== sourceOffer?.revision_id;
    if (sourceOffer) sourceRevision.current = sourceOffer.revision_id;
    if (form.getValues("source") !== "previous" ||
        (!externalChanged && (sourceOffer || !previous.isError && !(previous.data && !previousMatches)))) return;
    form.reset({ source: "", serviceId: "", name: "", amount: "", templateName: "" });
    setBatch(initialBatch(context)); setEntries([]);
    setSaveAsTemplate(false);
    setSourceWarning("Previous offer settings are unavailable. Choose another starting point.");
  }, [amendment, sourceOffer, previous.isError, previous.data, previousMatches, form, context]);

  const built = useMemo(() => materializeSessions(entries, context.timezone, new Date()), [entries, context.timezone]);
  const windows = built.value;
  const sessions = useMemo(() => windows?.map(({ starts_at, ends_at, court_ids }) =>
    ({ starts_at, ends_at, court_ids })) ?? [], [windows]);
  const unavailableCourt = entries.some((entry) => (entry.courtIds
    ?? (entry.courtId === "none" ? [] : [entry.courtId])).some((courtId) =>
      !context.courts.some((court) => court.court_id === courtId && court.is_enabled)));
  const availabilityBody = useMemo<CoachingAvailabilityCheckInput | null>(() =>
    sessions.length && !unavailableCourt ? { sessions } : null, [sessions, unavailableCourt]);
  const availability = useCoachingAvailabilityCheck(requestId, availabilityBody);
  const verdicts = availability.isCurrent && availability.data?.verdicts.length === sessions.length
    ? availability.data.verdicts : null;
  const refreshingPrior = values.source === "previous" && !previousAvailable;
  const busy = issue.isPending || form.formState.isSubmitting || refreshing;
  const totalAmount = moneyOrNull(values.amount);
  const canSend = Boolean(!busy && !refreshingPrior && values.name.trim() && totalAmount !== null
    && (!amendment || totalAmount === amendment.remaining_value)
    && (flexibleAmendment ? amendment?.remaining_units.length
      : windows?.length && !unavailableCourt && verdicts
        && availability.canAct() && verdicts.every((item) => item.available)));
  const orderedEntries = windows ? windows.map((window) => entries.find((entry) => entry.id === window.id)!)
    : [...entries].sort((left, right) => left.localStart.localeCompare(right.localStart) || left.id.localeCompare(right.id));
  const batchDay = Date.parse(`${batch.date}T00:00:00Z`);
  const selectedDayExcluded = batch.repeat === "Custom" && batch.unit === "week" && Number.isFinite(batchDay)
    && batch.weekdays.length > 0 && !batch.weekdays.includes(new Date(batchDay).getUTCDay());
  const selectedStart = selectedDayExcluded ? null : batch.exactStart ?? (batch.date && batch.time
    ? fromZonedDateTimeInput(`${batch.date}T${batch.time}`, context.timezone) : null);
  const saveTemplate = useMutation({ mutationFn: () => {
      const amount = moneyOrNull(values.amount);
      const minutes = Number(templateDefaults.duration);
      if (amount === null || !values.templateName.trim() || !entries.length
          || !Number.isSafeInteger(minutes) || minutes < 15 || minutes > 480
          || !templateDefaults.courtId || templateDefaults.courtId !== "none"
            && !context.courts.some((court) => court.court_id === templateDefaults.courtId && court.is_enabled))
        throw new ProposalInputError("Enter a valid template name, price, and schedule.");
      return coachApi.createProposalTemplate(context.coach_profile_id, {
        coach_profile_id: context.coach_profile_id, facility_id: facilityId,
        name: values.templateName.trim(), source_service_id: values.serviceId || null,
        service_name: values.name.trim(), total_amount: amount,
        // Preserve the priced session count; dated/irregular spacing is not reusable.
        schedule_kind: entries.length === 1 ? "single" : "weekly_series",
        session_count: entries.length,
        duration_minutes: minutes,
        preferred_court_ids: templateDefaults.courtId !== "none" ? [templateDefaults.courtId] : [],
      });
    },
    onSuccess: async () => { setSaved(true); await queryClient.invalidateQueries({ queryKey: ["coaching"] }); } });

  function chooseSource(source: string) {
    if (source === form.getValues("source")) return;
    if (source === "previous" && ((!sourceOffer && !previous.canAct()) || !previousTerms
        || !previousSessions?.length || !previousDuration || previousReason)) {
      setSourceWarning("Previous offer settings are unavailable. Choose another starting point.");
      return;
    }
    const service = source.startsWith("service:")
      ? context.services.find((candidate) => candidate.service_id === source.slice(8)) : null;
    if (source.startsWith("service:") && (!service || service.currency.toLowerCase() !== context.currency.toLowerCase())) {
      setSourceWarning("This service uses another currency. Choose a service priced in the facility currency or create a custom proposal.");
      return;
    }
    const template = source.startsWith("template:")
      ? context.templates.find((candidate) => candidate.template_id === source.slice(9)) : null;
    if (source.startsWith("template:") && !template) return;
    const clearNotice = entries.length ? "Existing scheduled sessions were cleared. Add new dates for these offer terms. " : "";
    form.setValue("source", source); form.setValue("templateName", "");
    form.clearErrors("root"); setEntries([]); setBatchError(null);
    setSaveAsTemplate(false); setSaved(false); setSourceWarning(clearNotice || null);
    if (source === "previous") {
      if (!previousTerms || !previousSessions?.length || !previousDuration) return;
      const sourceService = context.services.find((service) => service.service_id === previousTerms.source_service_id
        && service.currency.toLowerCase() === context.currency.toLowerCase());
      const courtIds = previousSessions.map((session) => session.court_ids);
      const oneCourt = courtIds.every((ids) => ids.length === 1 && ids[0] === courtIds[0]?.[0]);
      const noCourt = courtIds.every((ids) => ids.length === 0);
      const preferredCourt = oneCourt ? context.courts.find((court) => court.court_id === courtIds[0]?.[0] && court.is_enabled) : null;
      form.setValue("serviceId", sourceService?.service_id ?? ""); form.setValue("name", previousTerms.service_name);
      form.setValue("amount", String(previousTerms.total_amount));
      setBatch((current) => ({ ...current, duration: String(previousDuration),
        repeat: previousSessions.length > 1 ? "Weekly" : "Does not repeat",
        occurrences: String(previousSessions.length), ends: "After occurrences",
        courtId: preferredCourt?.court_id ?? (noCourt ? "none" : "") }));
      const warnings = [];
      if (previousTerms.source_service_id && !sourceService) warnings.push("The original service is unavailable; this is now a custom offer.");
      if (!preferredCourt && !noCourt) warnings.push("The previous court is unavailable or varied by session. Choose a current court.");
      if (previousDurations?.some((duration) => duration !== previousDuration))
        warnings.push("Previous sessions had different durations; review every new entry.");
      warnings.push(previousSessions.length > 1
        ? "Previous dates are not copied. The same session count starts weekly; edit dates as needed."
        : "Previous dates are not copied. Add a new session below.");
      setSourceWarning(clearNotice + warnings.join(" "));
    } else if (source.startsWith("service:")) {
      const item = service!;
      form.setValue("serviceId", item.service_id); form.setValue("name", item.name); form.setValue("amount", String(item.base_price));
      setBatch((current) => ({ ...current, duration: String(item.duration_minutes), repeat: "Does not repeat", courtId: "" }));
    } else if (source.startsWith("template:")) {
      const item = template!;
      const sourceService = context.services.find((service) => service.service_id === item.source_service_id
        && service.currency.toLowerCase() === context.currency.toLowerCase());
      form.setValue("serviceId", sourceService?.service_id ?? ""); form.setValue("name", item.service_name);
      form.setValue("amount", String(item.total_amount));
      const preferredCourt = context.courts.find((court) => court.court_id === item.preferred_court_ids[0] && court.is_enabled);
      setBatch((current) => ({ ...current, duration: String(item.duration_minutes),
        repeat: item.schedule_kind === "weekly_series" ? "Weekly" : "Does not repeat",
        occurrences: String(item.session_count), ends: "After occurrences", courtId: preferredCourt?.court_id ?? "" }));
      const warnings = [];
      if (item.source_service_id && !sourceService) warnings.push("The template's original service is unavailable. Review it as a custom offer before sending.");
      if (item.preferred_court_ids.length && !preferredCourt) warnings.push("The template's preferred court is unavailable. Choose a current court before sending.");
      warnings.push(item.session_count > 1
        ? "Template dates are not saved. Its priced session count starts weekly; edit dates as needed."
        : "Template dates are not saved. Choose a new session date below.");
      setSourceWarning(clearNotice + warnings.join(" "));
    } else form.setValue("serviceId", "");
  }

  function addBatch() {
    form.clearErrors("root");
    const expanded = expandScheduleBatch(batch, context.timezone, new Date(), MAX_PROPOSAL_SESSIONS - entries.length);
    if (expanded.error) { setBatchError(expanded.error); return; }
    const added = expanded.value!.map((entry) => ({ ...entry, id: crypto.randomUUID() }));
    const next = [...entries, ...added];
    const checked = materializeSessions(next, context.timezone, new Date());
    if (checked.error) { setBatchError(checked.error); return; }
    setEntries(next); setBatchError(null); setSaved(false);
  }

  function updateEntry(id: string, patch: Partial<SessionEntry>) {
    setEntries((current) => current.map((entry) => entry.id === id ? { ...entry, ...patch } : entry));
    setSaved(false);
  }

  async function submit(data: Values) {
    form.clearErrors("root");
    try {
      const amount = moneyOrNull(data.amount);
      if (amount === null || !canSend || (!flexibleAmendment && !windows?.length))
        throw new ProposalInputError("Check the proposal price and every session's current availability.");
      if (amendment && amount !== amendment.remaining_value)
        throw new ProposalInputError("A reschedule must keep the accepted remaining price.");
      const schedule: CoachingProposalInput["terms"]["schedule"] = flexibleAmendment
        && previousTerms?.schedule.kind === "flexible_package" && amendment
        ? { kind: "flexible_package", timezone: previousTerms.schedule.timezone,
          credit_count: amendment.remaining_units.length,
          session_duration_minutes: previousTerms.schedule.session_duration_minutes,
          booking_opens_at: previousTerms.schedule.booking_opens_at,
          booking_closes_at: previousTerms.schedule.booking_closes_at,
          sessions_complete_by: previousTerms.schedule.sessions_complete_by,
          allowed_court_ids: previousTerms.schedule.allowed_court_ids,
          required_court_count: previousTerms.schedule.required_court_count }
        : { kind: "fixed", timezone: context.timezone, sessions };
      const terms: CoachingProposalInput["terms"] = { source_service_id: data.serviceId || null,
        service_name: data.name.trim(), total_amount: amount, schedule };
      const fingerprint = JSON.stringify({ expected_revision: context.expected_revision, terms });
      if (attempt.current?.fingerprint !== fingerprint) attempt.current = { fingerprint, key: crypto.randomUUID() };
      await issue.mutateAsync([requestId, { expected_revision: context.expected_revision, idempotency_key: attempt.current.key,
        expected_cancellation_policy_id: context.cancellation.source_policy_id!,
        expected_cancellation_policy_version: context.cancellation.source_policy_version!,
        replaces_acceptance_id: amendment?.acceptance_id ?? null,
        expected_fulfillment_revision: amendment?.fulfillment_revision ?? null,
        expected_financial_witness: amendment?.financial_witness ?? null,
        terms }]);
      onClose(true);
    } catch (error) {
      form.setError("root", { message: error instanceof ProposalInputError ? error.message
        : "The proposal could not be sent. Refresh and try again." });
    }
  }

  return <>
    {refreshingPrior ? <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: "center" }}>
      <Typography role="status">Refreshing previous offer access…</Typography>
      <Button size="small" onClick={() => onClose()}>Cancel proposal</Button>
    </Stack> : null}
    <Stack component="form" aria-label="Proposal terms" spacing={2.5} noValidate
      sx={{ mt: 2, display: refreshingPrior ? "none" : undefined }} onSubmit={form.handleSubmit(submit)}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField label="Lesson name" size="small" disabled={busy || Boolean(amendment)} {...form.register("name")}
            error={Boolean(form.formState.errors.name)} helperText={form.formState.errors.name?.message} required fullWidth
            slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label={amendment ? "Accepted remaining price" : "Total price"} size="small" disabled={busy || Boolean(amendment)} {...form.register("amount")}
            error={Boolean(form.formState.errors.amount)} helperText={form.formState.errors.amount?.message} required
            sx={{ minWidth: { sm: 180 } }} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Currency" size="small" disabled value={context.currency.toUpperCase()} sx={{ width: { sm: 110 } }}
            slotProps={{ inputLabel: { shrink: true } }} />
        </Stack>
        {!amendment ? <TextField select label="Start from" size="small" value={values.source} disabled={busy}
          sx={{ maxWidth: 320 }} slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          onChange={(event) => chooseSource(event.target.value)}>
          <option value="">Custom proposal</option>
          {sourceOffer || previousRevisionId ? <option value="previous" disabled={!previousTerms || Boolean(previousReason)}>
            Previous offer settings{previousReason ? ` (${previousReason})` : previousAvailable ? " (dates not copied)" : " (loading)"}
          </option> : null}
          {context.templates.map((item) => <option key={item.template_id} value={`template:${item.template_id}`}>{item.name}</option>)}
          {context.services.map((item) => <option key={item.service_id} value={`service:${item.service_id}`}
            disabled={item.currency.toLowerCase() !== context.currency.toLowerCase()}>
            {item.name}{item.currency.toLowerCase() !== context.currency.toLowerCase()
              ? ` (${item.currency.toUpperCase()} price unavailable in ${context.currency.toUpperCase()})` : ""}
          </option>)}
        </TextField> : <Alert severity="info">Reschedule only: the accepted remaining price and participants will not change.</Alert>}
        {sourceWarning ? <Alert severity="warning">{sourceWarning}</Alert> : null}
      </Stack>

      {flexibleAmendment && previousTerms?.schedule.kind === "flexible_package" && amendment ? <><Divider />
      <Box component="section" aria-label="Package reschedule" sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Stack spacing={0.75}><Stack direction={{ xs: "column", sm: "row" }} spacing={1}
          sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
          <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>Remaining package</Typography>
          <Chip size="small" label={`${amendment.remaining_units.length} remaining ${amendment.remaining_units.length === 1 ? "credit" : "credits"}`} />
        </Stack>
        <Typography variant="body2">{previousTerms.schedule.session_duration_minutes} minutes per session · {previousTerms.schedule.required_court_count} courts required</Typography>
        <Typography variant="body2" color="text.secondary">
          Booking window and completion deadline stay unchanged. Booked remaining sessions return to available credits after the player accepts this reschedule.
        </Typography></Stack>
      </Box></> : <><Divider />
      <Stack spacing={2}>
        <Box><Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>Schedule</Typography>
          <Typography variant="body2" color="text.secondary">Add one time or a finite batch, then edit any session below. Times use {context.timezone}.</Typography></Box>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ alignItems: "flex-start" }}>
          <Stack spacing={1.5} sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
            <Stack direction="row" spacing={1}>
              <TextField label="Session date" type="date" size="small" value={batch.date} fullWidth disabled={busy || Boolean(amendment)}
                onChange={(event) => setBatch((current) => ({ ...current, date: event.target.value, exactStart: undefined }))}
                slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Start time" type="time" size="small" value={batch.time} fullWidth disabled={busy || Boolean(amendment)}
                onChange={(event) => setBatch((current) => ({ ...current, time: event.target.value, exactStart: undefined }))}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField label="Duration (minutes)" type="number" size="small" value={batch.duration} fullWidth disabled={busy || Boolean(amendment)}
                onChange={(event) => setBatch((current) => ({ ...current, duration: event.target.value }))}
                slotProps={{ htmlInput: { min: 15, max: 480, step: 1 } }} />
              <TextField select label="Court" size="small" value={batch.courtId} fullWidth disabled={busy || Boolean(amendment)}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                onChange={(event) => setBatch((current) => ({ ...current, courtId: event.target.value }))}>
                <option value="">Choose a court</option><option value="none">No court required</option>
                {context.courts.map((court) => <option key={court.court_id} value={court.court_id} disabled={!court.is_enabled}>{court.name}</option>)}
              </TextField>
            </Stack>
            <TextField select label="Repeat" size="small" value={batch.repeat} fullWidth disabled={busy || Boolean(amendment)}
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              onChange={(event) => setBatch((current) => ({ ...current,
                repeat: event.target.value as BatchDraft["repeat"] }))}>
              {(["Does not repeat", "Daily", "Weekly", "Custom"] as const).map((option) =>
                <option key={option} value={option}>{option}</option>)}
            </TextField>
            {batch.repeat === "Custom" ? <Stack direction="row" spacing={1}>
              <TextField label="Repeat every" type="number" size="small" value={batch.every} fullWidth disabled={busy}
                onChange={(event) => setBatch((current) => ({ ...current, every: event.target.value }))}
                slotProps={{ htmlInput: { min: 1, max: 12 } }} />
              <TextField select label="Repeat unit" size="small" value={batch.unit} fullWidth disabled={busy}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                onChange={(event) => setBatch((current) => ({ ...current, unit: event.target.value as "day" | "week" }))}>
                <option value="day">day</option><option value="week">week</option>
              </TextField>
            </Stack> : null}
            {batch.repeat === "Custom" && batch.unit === "week" ? <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Repeat on</Typography>
              <Stack role="group" aria-label="Repeat on" direction="row"
                spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
              {weekdays.map((day, index) => <Button key={day} size="small" aria-label={day}
                aria-pressed={batch.weekdays.includes(index)} variant={batch.weekdays.includes(index) ? "contained" : "outlined"}
                sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: "50%" }}
                onClick={() => setBatch((current) => ({ ...current, weekdays: current.weekdays.includes(index)
                  ? current.weekdays.filter((value) => value !== index) : [...current.weekdays, index] }))}>
                {day.slice(0, 1)}
              </Button>)}
              </Stack>
              {selectedDayExcluded ? <Typography variant="caption" color="text.secondary">
                The selected date is not a repeat day. This batch starts on the next selected weekday.
              </Typography> : null}
            </Stack> : null}
            {batch.repeat !== "Does not repeat" ? <Stack direction="row" spacing={1}>
              <TextField select label="Ends" size="small" value={batch.ends} fullWidth disabled={busy}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                onChange={(event) => setBatch((current) => ({ ...current, ends: event.target.value as BatchDraft["ends"] }))}>
                <option value="After occurrences">After occurrences</option><option value="On date">On date</option>
              </TextField>
              {batch.ends === "After occurrences"
                ? <TextField label="Occurrences" type="number" size="small" value={batch.occurrences} fullWidth disabled={busy}
                  onChange={(event) => setBatch((current) => ({ ...current, occurrences: event.target.value }))}
                  slotProps={{ htmlInput: { min: 1, max: MAX_PROPOSAL_SESSIONS } }} />
                : <TextField label="End date" type="date" size="small" value={batch.endDate} fullWidth disabled={busy}
                  onChange={(event) => setBatch((current) => ({ ...current, endDate: event.target.value }))}
                  slotProps={{ inputLabel: { shrink: true } }} />}
            </Stack> : null}
            {batchError ? <Alert severity="warning">{batchError}</Alert> : null}
            <Button size="small" variant="contained" disabled={busy || Boolean(amendment)} onClick={addBatch}>Add to proposal</Button>
          </Stack>
          <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
            <CoachingAvailabilityGrid requestId={requestId} timezone={context.timezone} courtId={batch.courtId}
              durationMinutes={Number(batch.duration)} selectedStart={selectedStart} focusedDate={batch.date}
              reservationTiming={amendment ? "apply" : "send"}
              onChoose={(instant) => { const local = toZonedDateTimeInput(instant, context.timezone);
                setBatch((current) => ({ ...current, date: local.slice(0, 10), time: local.slice(11, 16),
                  exactStart: new Date(instant).toISOString() })); setBatchError(null); }} />
          </Box>
        </Stack>
      </Stack>

      <Divider />
      <Box component="section" aria-label="Schedule preview">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>Schedule preview</Typography>
          <Chip size="small" label={`${entries.length} ${entries.length === 1 ? "session" : "sessions"}`} />
        </Stack>
        {!entries.length ? <Typography variant="body2" color="text.secondary">Choose a calendar time or enter a date, then add it to this proposal.</Typography> : null}
        {orderedEntries.map((entry, index) => {
          const window = windows?.[index];
          const verdict = verdicts?.[index];
          const status = verdict ? verdict.available ? "Available"
            : verdict.unavailable_reason?.replaceAll("_", " ") ?? "Unavailable"
            : windows ? "Checking availability…" : "Needs attention";
          const [date = "", time = ""] = entry.localStart.split("T");
          return <Box key={entry.id} sx={{ borderTop: 1, borderColor: "divider", py: 1.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {window ? `${index + 1}. ${sessionSummary(window, context)} · ${status}` : `Session ${index + 1} · ${status}`}
              </Typography>
              <Tooltip title={amendment ? "Accepted sessions cannot be removed" : "Remove session"}><span><IconButton size="small" aria-label={`Remove session ${index + 1}`}
                disabled={busy || Boolean(amendment)} onClick={() => { setEntries((current) => current.filter((item) => item.id !== entry.id)); setSaved(false); }}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton></span></Tooltip>
            </Stack>
            <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))",
              md: "minmax(150px, 1.25fr) minmax(125px, 1fr) minmax(110px, .8fr) minmax(180px, 1.6fr)" } }}>
              <TextField label={`Session ${index + 1} date`} type="date" size="small" value={date} disabled={busy}
                onChange={(event) => updateEntry(entry.id, { localStart: `${event.target.value}T${time}`, exactStart: undefined })}
                slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label={`Session ${index + 1} start time`} type="time" size="small" value={time} disabled={busy}
                onChange={(event) => updateEntry(entry.id, { localStart: `${date}T${event.target.value}`, exactStart: undefined })}
                slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label={`Session ${index + 1} duration`} type="number" size="small" value={entry.duration} disabled={busy || Boolean(amendment)}
                onChange={(event) => updateEntry(entry.id, { duration: event.target.value })}
                slotProps={{ htmlInput: { min: 15, max: 480, step: 1 } }} />
              {entry.courtIds ? <TextField label={`Session ${index + 1} courts`} size="small" disabled
                value={entry.courtIds.map((courtId) => context.courts.find((court) => court.court_id === courtId)?.name ?? "Court unavailable").join(", ")}
                helperText="Multi-court assignments stay together when rescheduled." fullWidth />
              : <TextField select label={`Session ${index + 1} court`} size="small" value={entry.courtId} disabled={busy}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }} fullWidth
                onChange={(event) => updateEntry(entry.id, { courtId: event.target.value })}>
                <option value="none">No court required</option>
                {context.courts.map((court) => <option key={court.court_id} value={court.court_id} disabled={!court.is_enabled}>{court.name}</option>)}
              </TextField>}
            </Box>
          </Box>;
        })}
        {built.error && entries.length ? <Alert severity="warning">{built.error}</Alert> : null}
        {unavailableCourt ? <Alert severity="warning">A selected court is no longer available. Choose another court.</Alert> : null}
        {availability.isError ? <Alert severity="error">Session availability could not be checked. Refresh and try again.</Alert> : null}
      </Box></>}

      {entries.length || flexibleAmendment ? <><Divider />
      <Stack spacing={1}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 700 }}>{flexibleAmendment && amendment
            ? `${amendment.remaining_units.length} remaining ${amendment.remaining_units.length === 1 ? "credit" : "credits"}`
            : `${entries.length} ${entries.length === 1 ? "session" : "sessions"}`} · One payment</Typography>
          <Typography sx={{ fontWeight: 700 }}>{totalAmount !== null ? formatMoney(totalAmount, context.currency) : "—"}</Typography>
        </Stack>
        <Box component="section" aria-label="Cancellation policy">
          <Typography variant="subtitle2">Facility cancellation policy</Typography>
          {coachingCancellationSummary(amendment && previousTerms
            ? previousTerms.cancellation : context.cancellation).map((line) =>
            <Typography key={line} variant="body2">{line}</Typography>)}
        </Box>
        {!amendment ? <FormControlLabel control={<Switch checked={saveAsTemplate} disabled={busy}
          onChange={(_, checked) => {
            setSaveAsTemplate(checked); setSaved(false);
            form.setValue("templateName", checked ? values.name : "");
            if (checked) {
              const first = orderedEntries[0];
              setTemplateDefaults({ duration: first?.duration ?? batch.duration,
                courtId: first?.courtId ?? batch.courtId });
            }
          }} />}
          label="Save offer details as a template" /> : null}
        {saveAsTemplate ? <Stack direction={{ xs: "column", lg: "row" }} spacing={1}>
          <TextField label="Template name" size="small" {...form.register("templateName")} fullWidth />
          <TextField label="Template default duration" type="number" size="small" value={templateDefaults.duration}
            onChange={(event) => { setTemplateDefaults((current) => ({ ...current, duration: event.target.value })); setSaved(false); }}
            slotProps={{ htmlInput: { min: 15, max: 480, step: 1 } }} sx={{ minWidth: { sm: 170 } }} />
          <TextField select label="Template default court" size="small" value={templateDefaults.courtId}
            onChange={(event) => { setTemplateDefaults((current) => ({ ...current, courtId: event.target.value })); setSaved(false); }}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }} sx={{ minWidth: { sm: 180 } }}>
            <option value="">Choose a court</option><option value="none">No court required</option>
            {context.courts.map((court) => <option key={court.court_id} value={court.court_id} disabled={!court.is_enabled}>{court.name}</option>)}
          </TextField>
          <Button size="small" variant="outlined" disabled={saveTemplate.isPending || saved || !values.templateName.trim() || !entries.length
            || !templateDefaults.courtId || !Number.isSafeInteger(Number(templateDefaults.duration))
            || Number(templateDefaults.duration) < 15 || Number(templateDefaults.duration) > 480}
            onClick={() => saveTemplate.mutate()}>{saved ? "Saved" : "Save template"}</Button>
        </Stack> : null}
        {saveAsTemplate ? <Typography variant="caption" color="text.secondary">
          Templates keep the total price and session count. {entries.length > 1
            ? "New dates start weekly and can be edited; irregular spacing is not saved."
            : "Choose a new date when reusing this offer."}
        </Typography> : null}
        {saveTemplate.isError ? <Alert severity="warning">The template could not be saved. You can still send this proposal.</Alert> : null}
      </Stack></> : null}
      {form.formState.errors.root ? <Alert severity="error">{form.formState.errors.root.message}</Alert> : null}
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
        <Button size="small" onClick={() => onClose()}>Cancel</Button>
        <Button size="small" type="submit" variant="contained" disabled={!canSend}>{amendment ? "Send reschedule proposal" : "Send proposal"}</Button>
      </Stack>
    </Stack>
  </>;
}
